import { useMemo } from "react";
import { Wrench } from "lucide-react";
import { SectionCard, SectionHeader, KpiChip, ChartCard, MiniPie, StackedBar, pctOf, SECTION_ACCENTS } from "./dashboardUi";
import { TICKET_TYPE_LABEL, CLOSED_STATUSES, isTicketInProblem } from "../../../lib/ticketUtils";

const STATUS_LABEL = {
  open: "Open", in_progress: "In Progress", on_hold: "On Hold",
  resolution_support_required: "Support Req.", completed: "Completed", closed: "Closed",
};
const C = SECTION_ACCENTS.ticket.chart;
const STATUS_BARS = [
  { key: "open", label: "Open", color: C[0] },
  { key: "in_progress", label: "In Progress", color: C[1] },
  { key: "on_hold", label: "On Hold", color: C[2] },
  { key: "resolution_support_required", label: "Support Req.", color: C[3] },
  { key: "completed", label: "Completed", color: C[4] },
  { key: "closed", label: "Closed", color: C[5] },
];

export default function TicketSection({ tickets, delay }) {
  const insights = useMemo(() => {
    const statusCounts = {};
    const typeCounts = {};
    const byTypeStatus = {};
    let resolved = 0, breached = 0, problem = 0;
    for (const t of tickets) {
      statusCounts[t.status] = (statusCounts[t.status] || 0) + 1;
      typeCounts[t.type] = (typeCounts[t.type] || 0) + 1;
      if (CLOSED_STATUSES.includes(t.status)) resolved++;
      if (t.sla_breached) breached++;
      if (isTicketInProblem(t)) problem++;
      const tlabel = TICKET_TYPE_LABEL[t.type] || t.type;
      byTypeStatus[tlabel] = byTypeStatus[tlabel] || { name: tlabel };
      byTypeStatus[tlabel][t.status] = (byTypeStatus[tlabel][t.status] || 0) + 1;
    }
    return {
      total: tickets.length,
      resolvedPct: pctOf(resolved, tickets.length),
      breachedPct: pctOf(breached, tickets.length),
      problem,
      statusData: Object.entries(statusCounts).map(([k, value]) => ({ name: STATUS_LABEL[k] || k, value })),
      typeData: Object.entries(typeCounts).map(([k, value]) => ({ name: TICKET_TYPE_LABEL[k] || k, value })),
      typeStatusData: Object.values(byTypeStatus),
    };
  }, [tickets]);

  return (
    <SectionCard accent="ticket" delay={delay} testid="dashboard-ticket-section">
      <SectionHeader icon={Wrench} accent="ticket" title="Service Tickets" subtitle="Resolution status across every ticket type" />
      <div className="flex items-center gap-1 flex-wrap mb-3">
        <KpiChip label="Total Tickets" value={insights.total} tone="bg-[#B91C1C] text-white" to="/crm/tickets" />
        <KpiChip label="% Resolved" value={`${insights.resolvedPct}%`} tone="bg-emerald-50 text-emerald-800" to="/crm/tickets?status=completed" />
        <KpiChip label="SLA Breach %" value={`${insights.breachedPct}%`} tone="bg-amber-50 text-amber-800" to="/crm/tickets?breached=true" />
        <KpiChip label="Tickets In Problem" value={insights.problem} tone="bg-red-50 text-red-700" to="/crm/tickets?status=resolution_support_required" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-ticket-charts">
        <ChartCard title="Tickets by Type" testid="chart-ticket-type"><MiniPie data={insights.typeData} colors={C} to="/crm/tickets" /></ChartCard>
        <ChartCard title="Resolution Status" testid="chart-ticket-resolution"><MiniPie data={insights.statusData} colors={C} to="/crm/tickets" /></ChartCard>
        <ChartCard title="Type × Resolution Status" testid="chart-ticket-type-status">
          <StackedBar data={insights.typeStatusData} bars={STATUS_BARS} to="/crm/tickets" />
        </ChartCard>
      </div>
    </SectionCard>
  );
}
